import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { parseCallTranscript } from '@/mixins/CallHistory/parseCallTranscript.js'
import { readOrgAiEngine } from '@/utils/agentOtherSettings'
import { formatCallDuration, formatIsoDate, formatTimeAmPm, formatPhoneNumber } from '@/utils/format'
import {
  resolveCallRecordingS3Key,
  loadCallRecordingPlaybackUrl,
  revokeCallRecordingPlaybackUrl,
} from '@/utils/callRecording'

const SEGMENT_ICONS = {
  agent: 'mdi-robot-outline',
  user: 'mdi-account-outline',
  unknown: 'mdi-message-text-outline',
}

function formatPlayerTime(seconds) {
  const n = Number(seconds)
  if (!Number.isFinite(n) || n < 0) return '0:00'
  const total = Math.floor(n)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function useCallIntelligence(call, organization, isOpen) {
  const audioRef = ref(null)
  const isPlaying = ref(false)
  const currentTime = ref(0)
  const duration = ref(0)
  const playbackUrl = ref(null)
  const recordingLoading = ref(false)
  const recordingError = ref(null)

  const recordingS3Key = computed(() => resolveCallRecordingS3Key(call.value))

  const transcriptSegments = computed(() =>
    parseCallTranscript(
      call.value?.transcript,
      call.value?.start_time ?? null,
    ),
  )

  const chatLines = computed(() =>
    transcriptSegments.value.map((seg, index) => {
      const role = seg.kind === 'user' ? 'user' : 'agent'
      return {
        id: seg.id ?? `line-${index}`,
        role,
        text: seg.text,
        timeLabel: seg.timeLabel,
        senderLabel:
          seg.speakerLabel || (role === 'user' ? 'Customer' : 'AI Agent'),
        icon: SEGMENT_ICONS[seg.kind] ?? SEGMENT_ICONS.unknown,
      }
    }),
  )

  const orgName = computed(() => {
    const name = organization.value?.org_name?.trim()
    return name || 'Your organisation'
  })

  const aiAgentLabel = computed(() => {
    const engine = readOrgAiEngine(organization.value)
    if (engine) return engine.toUpperCase()
    return 'AI Agent'
  })

  const headerSubtitle = computed(() => {
    const parts = []
    const from = call.value?.from_number
    if (from && from !== '—') parts.push(formatPhoneNumber(from))
    parts.push(orgName.value)
    return parts.join(' · ')
  })

  const durationLabel = computed(() =>
    formatCallDuration(call.value?.duration_seconds),
  )

  const overviewItems = computed(() => [
    { label: 'Organization', value: orgName.value },
    { label: 'Caller', value: call.value?.from_number && call.value.from_number !== '—'
        ? formatPhoneNumber(call.value.from_number)
        : '—' },
    { label: 'AI agent', value: aiAgentLabel.value },
    { label: 'Duration', value: durationLabel.value },
    {
      label: 'Call started',
      value: formatTimelineTime(call.value?.start_time),
    },
  ])

  const waveformBars = computed(() =>
    Array.from({ length: 48 }, (_, i) => {
      const wave = Math.sin(i * 0.45) * 0.35 + Math.cos(i * 0.18) * 0.25
      return Math.max(0.18, Math.min(1, 0.42 + wave))
    }),
  )

  const progressPercent = computed(() => {
    if (!duration.value) return 0
    return Math.min(100, (currentTime.value / duration.value) * 100)
  })

  function formatTimelineTime(raw) {
    if (raw == null || raw === '') return '—'
    const date = formatIsoDate(raw)
    const time = formatTimeAmPm(raw)
    if (date === '—' || time === '—') return '—'
    return `${date}, ${time}`
  }

  function onTimeUpdate() {
    const el = audioRef.value
    if (!el) return
    currentTime.value = el.currentTime || 0
  }

  function onLoadedMetadata() {
    const el = audioRef.value
    if (!el) return
    const mediaDuration = Number(el.duration)
    duration.value = Number.isFinite(mediaDuration) && mediaDuration > 0
      ? mediaDuration
      : Number(call.value?.duration_seconds) || 0
  }

  function onAudioEnded() {
    isPlaying.value = false
  }

  function onAudioError() {
    isPlaying.value = false
    recordingError.value = 'Unable to play this recording.'
  }

  async function togglePlayback() {
    const el = audioRef.value
    if (!el || !playbackUrl.value || recordingLoading.value) return

    try {
      if (el.paused) {
        await el.play()
        isPlaying.value = true
      } else {
        el.pause()
        isPlaying.value = false
      }
    } catch (err) {
      isPlaying.value = false
      recordingError.value = 'Unable to play this recording.'
      console.error('Recording playback failed', err)
    }
  }

  function seekAudio(event) {
    const el = audioRef.value
    if (!el || !duration.value) return
    const value = Number(event.target.value)
    if (!Number.isFinite(value)) return
    el.currentTime = value
    currentTime.value = value
  }

  function pausePlayback() {
    const el = audioRef.value
    if (el) {
      el.pause()
      el.currentTime = 0
    }
    isPlaying.value = false
    currentTime.value = 0
  }

  function revokeCurrentPlaybackUrl() {
    revokeCallRecordingPlaybackUrl(playbackUrl.value)
    playbackUrl.value = null
  }

  let resolveToken = 0

  async function loadPlaybackUrl(s3Key) {
    const token = ++resolveToken
    recordingLoading.value = true
    recordingError.value = null
    revokeCurrentPlaybackUrl()
    pausePlayback()

    if (!s3Key) {
      recordingLoading.value = false
      return
    }

    try {
      const resolved = await loadCallRecordingPlaybackUrl(call.value || s3Key)
      if (token !== resolveToken) {
        revokeCallRecordingPlaybackUrl(resolved)
        return
      }

      if (!resolved) {
        recordingError.value = 'Unable to load this recording.'
        return
      }

      playbackUrl.value = resolved
    } catch (err) {
      if (token !== resolveToken) return
      recordingError.value = 'Unable to load this recording.'
      console.error('Recording download failed', err)
    } finally {
      if (token === resolveToken) {
        recordingLoading.value = false
      }
    }
  }

  watch(recordingS3Key, (s3Key) => {
    void loadPlaybackUrl(s3Key)
  }, { immediate: true })

  watch(isOpen, (open) => {
    if (!open) pausePlayback()
  })

  watch(call, () => {
    pausePlayback()
    duration.value = Number(call.value?.duration_seconds) || 0
  })

  onBeforeUnmount(() => {
    pausePlayback()
    revokeCurrentPlaybackUrl()
  })

  return {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    recordingS3Key,
    playbackUrl,
    recordingLoading,
    recordingError,
    chatLines,
    headerSubtitle,
    overviewItems,
    waveformBars,
    progressPercent,
    formatPlayerTime,
    togglePlayback,
    seekAudio,
    onTimeUpdate,
    onLoadedMetadata,
    onAudioEnded,
    onAudioError,
    pausePlayback,
  }
}
